/**
 * Enhanced Mental Health Assessment Component
 * Premium UX, Production-Grade, Therapist/Psychiatrist Focused
 * Comprehensive mental health screening tools: PHQ-9, GAD-7, PCL-5, PHQ-15, Custom assessments
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchMentalHealthAssessments,
  addMentalHealthAssessment,
  updateMentalHealthAssessment,
  deleteMentalHealthAssessment,
} from '../../features/patients/patientSlice';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  Button,
  Chip,
  LinearProgress,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Psychology,
  Assessment,
  TrendingUp,
  TrendingDown,
  History,
  Add,
  Edit,
  Delete,
  Download,
  Print,
  Warning,
  CheckCircle,
  BarChart,
  Timeline,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { styled } from '@mui/material/styles';
import { format, parseISO } from 'date-fns';
import {
  LineChart,
  Line,
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: alpha(theme.palette.background.paper, 0.95),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const AssessmentCard = styled(Paper)(({ theme, severity }) => {
  const colors = {
    minimal: { bg: theme.palette.success.main, text: theme.palette.success.contrastText },
    mild: { bg: theme.palette.info.main, text: theme.palette.info.contrastText },
    moderate: { bg: theme.palette.warning.main, text: theme.palette.warning.contrastText },
    severe: { bg: theme.palette.error.main, text: theme.palette.error.contrastText },
  };
  const color = colors[severity] || colors.minimal;

  return {
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius * 2,
    background: alpha(color.bg, 0.1),
    border: `2px solid ${alpha(color.bg, 0.3)}`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.02)',
      boxShadow: theme.shadows[4],
    },
  };
});

// PHQ-9 Assessment (Depression)
const PHQ9_QUESTIONS = [
  "Little interest or pleasure in doing things",
  "Feeling down, depressed, or hopeless",
  "Trouble falling or staying asleep, or sleeping too much",
  "Feeling tired or having little energy",
  "Poor appetite or overeating",
  "Feeling bad about yourself - or that you are a failure or have let yourself or your family down",
  "Trouble concentrating on things, such as reading the newspaper or watching television",
  "Moving or speaking so slowly that other people could have noticed. Or the opposite - being so fidgety or restless that you have been moving around a lot more than usual",
  "Thoughts that you would be better off dead, or of hurting yourself",
];

// GAD-7 Assessment (Anxiety)
const GAD7_QUESTIONS = [
  "Feeling nervous, anxious, or on edge",
  "Not being able to stop or control worrying",
  "Worrying too much about different things",
  "Trouble relaxing",
  "Being so restless that it is hard to sit still",
  "Becoming easily annoyed or irritable",
  "Feeling afraid, as if something awful might happen",
];

// PCL-5 Assessment (PTSD) - shortened version
const PCL5_QUESTIONS = [
  "Repeated, disturbing, and unwanted memories of the stressful experience?",
  "Repeated, disturbing dreams of the stressful experience?",
  "Suddenly feeling or acting as if the stressful experience were actually happening again?",
  "Feeling very upset when something reminded you of the stressful experience?",
  "Having physical reactions when something reminded you of the stressful experience?",
];

// PHQ-15 Assessment (Somatic Symptoms)
const PHQ15_QUESTIONS = [
  "Stomach pain",
  "Back pain",
  "Pain in your arms, legs, or joints",
  "Menstrual cramps or other problems with your periods",
  "Headaches",
  "Chest pain",
  "Dizziness",
  "Fainting spells",
  "Feeling your heart pound or race",
  "Shortness of breath",
  "Pain or problems during sexual intercourse",
  "Constipation, loose bowels, or diarrhea",
  "Nausea, gas, or indigestion",
  "Feeling tired or having low energy",
  "Trouble sleeping",
];

const SCORE_SCALES = {
  phq9: {
    minimal: { min: 0, max: 4, label: 'Minimal', color: 'success' },
    mild: { min: 5, max: 9, label: 'Mild', color: 'info' },
    moderate: { min: 10, max: 14, label: 'Moderate', color: 'warning' },
    moderately_severe: { min: 15, max: 19, label: 'Moderately Severe', color: 'error' },
    severe: { min: 20, max: 27, label: 'Severe', color: 'error' },
  },
  gad7: {
    minimal: { min: 0, max: 4, label: 'Minimal', color: 'success' },
    mild: { min: 5, max: 9, label: 'Mild', color: 'info' },
    moderate: { min: 10, max: 14, label: 'Moderate', color: 'warning' },
    severe: { min: 15, max: 21, label: 'Severe', color: 'error' },
  },
  pcl5: {
    minimal: { min: 0, max: 10, label: 'Minimal', color: 'success' },
    mild: { min: 11, max: 20, label: 'Mild', color: 'info' },
    moderate: { min: 21, max: 30, label: 'Moderate', color: 'warning' },
    severe: { min: 31, max: 40, label: 'Severe', color: 'error' },
  },
  phq15: {
    minimal: { min: 0, max: 4, label: 'Minimal', color: 'success' },
    mild: { min: 5, max: 9, label: 'Mild', color: 'info' },
    moderate: { min: 10, max: 14, label: 'Moderate', color: 'warning' },
    severe: { min: 15, max: 30, label: 'Severe', color: 'error' },
  },
};

const EnhancedMentalHealthAssessment = ({ patientId: propPatientId, role = 'doctor' }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { id: paramPatientId } = useParams();
  const finalPatientId = propPatientId || paramPatientId;

  // Redux state
  const patientsState = useSelector((state) => state.patients);
  const savedAssessments = patientsState?.mentalHealth?.assessments || [];
  const loading = patientsState?.mentalHealth?.loading || false;
  const error = patientsState?.mentalHealth?.error || null;

  // Local state
  const [activeTab, setActiveTab] = useState(0);
  const [activeAssessment, setActiveAssessment] = useState(null);
  const [assessmentResults, setAssessmentResults] = useState({
    phq9: Array(9).fill(0),
    gad7: Array(7).fill(0),
    pcl5: Array(5).fill(0),
    phq15: Array(15).fill(0),
  });
  const [showAssessmentDialog, setShowAssessmentDialog] = useState(false);
  const [currentAssessmentType, setCurrentAssessmentType] = useState(null);
  const [saving, setSaving] = useState(false);

  // Fetch assessments on mount
  useEffect(() => {
    if (finalPatientId) {
      dispatch(fetchMentalHealthAssessments(finalPatientId));
    }
  }, [dispatch, finalPatientId]);

  // Calculate scores
  const calculateScore = (type, answers) => {
    const total = answers.reduce((sum, val) => sum + val, 0);
    const scale = SCORE_SCALES[type];
    let severity = 'minimal';
    let severityLabel = 'Minimal';
    let severityColor = 'success';

    for (const [key, range] of Object.entries(scale)) {
      if (total >= range.min && total <= range.max) {
        severity = key;
        severityLabel = range.label;
        severityColor = range.color;
        break;
      }
    }

    return { total, severity, severityLabel, severityColor };
  };


  const handleAnswerChange = (type, index, value) => {
    setAssessmentResults(prev => ({
      ...prev,
      [type]: prev[type].map((val, i) => i === index ? parseInt(value) : val),
    }));
  };

  const handleSubmitAssessment = useCallback(async (type) => {
    if (!finalPatientId) {
      alert('Patient ID is required');
      return;
    }

    const score = calculateScore(type, assessmentResults[type]);
    
    // Prepare assessment data for backend
    const assessmentData = {
      assessment_type: type,
      total_score: score.total,
      severity: score.severity,
      responses: assessmentResults[type].map((value, index) => ({
        question: index + 1,
        answer: value,
      })),
      assessment_date: new Date().toISOString().split('T')[0],
      notes: '',
    };

    setSaving(true);
    try {
      await dispatch(
        addMentalHealthAssessment({
          patientId: finalPatientId,
          assessmentData,
        })
      ).unwrap();
      
      // Refresh assessments
      await dispatch(fetchMentalHealthAssessments(finalPatientId));
      
      // Reset answers
      setAssessmentResults(prev => ({
        ...prev,
        [type]: Array(assessmentResults[type].length).fill(0),
      }));
      setShowAssessmentDialog(false);
      setCurrentAssessmentType(null);
    } catch (error) {
      console.error('Failed to save assessment:', error);
      alert(`Failed to save assessment: ${error?.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  }, [finalPatientId, dispatch, assessmentResults]);

  const handleStartAssessment = (type) => {
    setCurrentAssessmentType(type);
    setShowAssessmentDialog(true);
  };

  // Transform backend assessments to component format
  const assessmentHistory = useMemo(() => {
    return savedAssessments.map(assessment => {
      const assessmentType = assessment.assessment_type || assessment.type;
      const responses = assessment.responses || [];
      const answers = Array.isArray(responses) 
        ? responses.map(r => typeof r === 'number' ? r : (r.answer || r.value || 0))
        : [];
      const scoreObj = calculateScore(assessmentType, answers);
      return {
        id: assessment.id,
        type: assessmentType,
        date: assessment.assessment_date || assessment.created_at,
        score: assessment.total_score || scoreObj.total,
        severity: assessment.severity || scoreObj.severity,
        severityLabel: assessment.severity_display || scoreObj.severityLabel,
        severityColor: scoreObj.severityColor,
        answers: answers,
      };
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [savedAssessments]);

  // Get latest scores for each assessment type from history
  const getLatestScore = useCallback((type) => {
    const latest = assessmentHistory.find(item => item.type === type);
    if (!latest) return { total: 0, severity: 'minimal', severityLabel: 'Minimal', severityColor: 'success' };
    const scoreObj = calculateScore(type, latest.answers);
    return {
      total: latest.score || scoreObj.total,
      severity: latest.severity || scoreObj.severity,
      severityLabel: latest.severityLabel || scoreObj.severityLabel,
      severityColor: latest.severityColor || scoreObj.severityColor,
    };
  }, [assessmentHistory]);

  // Update scores from history or current input
  const phq9Score = useMemo(() => {
    const latest = getLatestScore('phq9');
    const current = calculateScore('phq9', assessmentResults.phq9);
    return latest.total > 0 && assessmentResults.phq9.every(v => v === 0) ? latest : current;
  }, [assessmentHistory, assessmentResults.phq9, getLatestScore]);
  
  const gad7Score = useMemo(() => {
    const latest = getLatestScore('gad7');
    const current = calculateScore('gad7', assessmentResults.gad7);
    return latest.total > 0 && assessmentResults.gad7.every(v => v === 0) ? latest : current;
  }, [assessmentHistory, assessmentResults.gad7, getLatestScore]);
  
  const pcl5Score = useMemo(() => {
    const latest = getLatestScore('pcl5');
    const current = calculateScore('pcl5', assessmentResults.pcl5);
    return latest.total > 0 && assessmentResults.pcl5.every(v => v === 0) ? latest : current;
  }, [assessmentHistory, assessmentResults.pcl5, getLatestScore]);
  
  const phq15Score = useMemo(() => {
    const latest = getLatestScore('phq15');
    const current = calculateScore('phq15', assessmentResults.phq15);
    return latest.total > 0 && assessmentResults.phq15.every(v => v === 0) ? latest : current;
  }, [assessmentHistory, assessmentResults.phq15, getLatestScore]);

  const renderAssessmentForm = (type, questions) => {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          {type.toUpperCase()} Assessment
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please answer each question based on how you've felt over the past 2 weeks.
        </Typography>
        <FormControl component="fieldset" fullWidth>
          <Stack spacing={3}>
            {questions.map((question, index) => (
              <Paper key={index} sx={{ p: 2 }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 500 }}>
                  {index + 1}. {question}
                </FormLabel>
                <RadioGroup
                  row
                  value={assessmentResults[type][index].toString()}
                  onChange={(e) => handleAnswerChange(type, index, e.target.value)}
                >
                  <FormControlLabel value="0" control={<Radio />} label="Not at all" />
                  <FormControlLabel value="1" control={<Radio />} label="Several days" />
                  <FormControlLabel value="2" control={<Radio />} label="More than half the days" />
                  <FormControlLabel value="3" control={<Radio />} label="Nearly every day" />
                </RadioGroup>
              </Paper>
            ))}
          </Stack>
        </FormControl>
      </Box>
    );
  };

  // Prepare chart data for history
  const chartData = useMemo(() => {
    return assessmentHistory
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map(item => {
        try {
          const date = item.date ? (parseISO(item.date) || new Date(item.date)) : new Date();
          return {
            date: format(date, 'MMM dd'),
            score: item.score,
            type: item.type.toUpperCase(),
          };
        } catch (e) {
          return {
            date: format(new Date(), 'MMM dd'),
            score: item.score,
            type: item.type.toUpperCase(),
          };
        }
      });
  }, [assessmentHistory]);

  const assessments = [
    {
      id: 'phq9',
      name: 'PHQ-9',
      fullName: 'Patient Health Questionnaire-9',
      description: 'Depression screening tool',
      questions: PHQ9_QUESTIONS,
      score: phq9Score,
      maxScore: 27,
    },
    {
      id: 'gad7',
      name: 'GAD-7',
      fullName: 'Generalized Anxiety Disorder-7',
      description: 'Anxiety screening tool',
      questions: GAD7_QUESTIONS,
      score: gad7Score,
      maxScore: 21,
    },
    {
      id: 'pcl5',
      name: 'PCL-5',
      fullName: 'PTSD Checklist for DSM-5',
      description: 'PTSD screening tool',
      questions: PCL5_QUESTIONS,
      score: pcl5Score,
      maxScore: 40,
    },
    {
      id: 'phq15',
      name: 'PHQ-15',
      fullName: 'Patient Health Questionnaire-15',
      description: 'Somatic symptom severity',
      questions: PHQ15_QUESTIONS,
      score: phq15Score,
      maxScore: 30,
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Mental Health Assessments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comprehensive mental health screening and progress tracking
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<Download />}>
            Export Reports
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {typeof error === 'string' ? error : error?.message || 'An error occurred'}
        </Alert>
      )}

      {/* Loading State */}
      {loading && savedAssessments.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      )}

      {/* Assessment Cards */}
      {(!loading || savedAssessments.length > 0) && (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {assessments.map((assessment, index) => (
          <Grid item xs={12} sm={6} md={3} key={assessment.id}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
            >
              <AssessmentCard severity={assessment.score.severity}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      {assessment.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {assessment.fullName}
                    </Typography>
                  </Box>
                  <Psychology color={assessment.score.severityColor} />
                </Box>
                <Divider sx={{ my: 2 }} />
                {assessment.score.total > 0 ? (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Score</Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {assessment.score.total} / {assessment.maxScore}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(assessment.score.total / assessment.maxScore) * 100}
                        color={assessment.score.severityColor}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Chip
                      label={assessment.score.severityLabel}
                      color={assessment.score.severityColor}
                      sx={{ mb: 2 }}
                    />
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Not assessed yet
                  </Typography>
                )}
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handleStartAssessment(assessment.id)}
                  startIcon={<Assessment />}
                >
                  {assessment.score.total > 0 ? 'Re-assess' : 'Start Assessment'}
                </Button>
              </AssessmentCard>
            </motion.div>
          </Grid>
        ))}
      </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Assessment History" icon={<History />} />
          <Tab label="Progress Charts" icon={<BarChart />} />
          <Tab label="Trend Analysis" icon={<Timeline />} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 0 && (
          <motion.div
            key="history"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            <StyledCard>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Assessment History
                </Typography>
                <Divider sx={{ mb: 3 }} />
                {assessmentHistory.length > 0 ? (
                  <Stack spacing={2}>
                    {assessmentHistory.map((item) => (
                      <Paper key={item.id} sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {item.type.toUpperCase()} Assessment
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(item.date), 'MMM dd, yyyy hh:mm a')}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Chip
                              label={`Score: ${item.score}`}
                              color={item.severityColor}
                              variant="outlined"
                            />
                            <Chip
                              label={item.severityLabel}
                              color={item.severityColor}
                            />
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No assessments completed yet
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </StyledCard>
          </motion.div>
        )}

        {activeTab === 1 && (
          <motion.div
            key="charts"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            <StyledCard>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Progress Over Time
                </Typography>
                <Divider sx={{ mb: 3 }} />
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="score" 
                        stroke={theme.palette.primary.main} 
                        strokeWidth={2}
                        name="Score"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      Complete assessments to see progress charts
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </StyledCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Assessment Dialog */}
      <Dialog
        open={showAssessmentDialog}
        onClose={() => setShowAssessmentDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {currentAssessmentType && assessments.find(a => a.id === currentAssessmentType)?.fullName}
        </DialogTitle>
        <DialogContent>
          {currentAssessmentType && renderAssessmentForm(
            currentAssessmentType,
            assessments.find(a => a.id === currentAssessmentType)?.questions || []
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAssessmentDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => currentAssessmentType && handleSubmitAssessment(currentAssessmentType)}
          >
            Submit Assessment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnhancedMentalHealthAssessment;

